import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Button, Card, Form, MenuItem, SelectField, DateField } from "@curves/ui-base";
import { Grid } from "@mui/material";
import { parseDate } from "@util/moment";
import { useAccountBookContext } from "./components/AccountBookContext";
import { initBookmark } from "./staticData";
import { isEmpty, objectToQueryString, paramsToObject } from "@util/utils";
import { ACCOUNT_BOOK_ORDERS_OVERVIEW_URL } from "@icoach/router/routerPath";
import { useHistory } from "react-router-dom";
import useCampaignApi from "@apis/useCampaignApi";

/**
 * 抵用券訂單搜尋欄組件
 * 專門處理抵用券訂單的搜尋邏輯，與一般訂單分離
 * 搜尋欄位：抵用券、月份
 * API：postCampaignOverviewApi
 */
const AccountBookCampaignOrderOverviewSearchBar = props => {
	const { className } = props;
	const history = useHistory();
	const { postCampaignOverviewApi, getCampaignOverviewOptionsApi } = useCampaignApi();
	const { activeTab, setTableData, bookmark, setBookmarkNum, setRefresh } = useAccountBookContext();
	const [paramsData, setParamsData] = useState({});
	const { campaignID, campaignMonth = parseDate(new Date(), "YYYY-MM") } = paramsData || {};
	const [campaignOptions, setCampaignOptions] = useState([]);
	const campaignMonthRef = useRef(null);
	const campaignRef = useRef(null);
	const preloadRef = useRef(false);
	const refreshQueryRef = useRef(false);

	/**
	 * 處理搜尋按鈕點擊
	 */
	const handleSearchOnClick = () => {
		const params = getParams(initBookmark(), activeTab);
		preloadRef.current = false;
		getOverviewApi(params);
		setBookmarkNum(1);
	};

	/**
	 * 更新 URL 查詢參數
	 */
	const updateUrlWithSearchQuery = params => {
		let url = ACCOUNT_BOOK_ORDERS_OVERVIEW_URL;
		if (!isEmpty(params)) url += objectToQueryString(params);
		history.replace(url);
	};

	const getQueryStringParams = () => {
		const search = history.location.search;
		const { campaignID = "", campaignMonth = parseDate(new Date(), "YYYY-MM") } = paramsToObject(search);
		return { campaignID, campaignMonth };
	};

	/**
	 * 獲取搜尋參數
	 * 抵用券訂單參數：pageIndex, pageSize, campaignID, campaignMonth
	 */
	const getParams = (pageInfo = {}, currentOrderSourceType) => {
		const result = {};

		if (pageInfo) {
			Object.assign(result, pageInfo);
		}

		// 抵用券訂單參數
		if (currentOrderSourceType) {
			Object.assign(result);
		}

		// 抵用券月份參數
		if (campaignMonthRef.current && campaignMonthRef.current.getResult) {
			Object.assign(result, { campaignMonth: campaignMonthRef.current.getResult("YYYY-MM") });
		}

		// 抵用券 campaignID 參數
		if (campaignRef.current && campaignRef.current.getResult) {
			Object.assign(result, { campaignID: campaignRef.current.getResult() || "" });
		}

		return result;
	};

	/**
	 * 獲取抵用券選項
	 */
	const getOptionsApi = async () => {
		const campaignRes = await getCampaignOverviewOptionsApi();

		// 處理抵用券選項
		if (campaignRes && campaignRes.campaignOptions) {
			const options = campaignRes.campaignOptions;
			setCampaignOptions(options);
		}
	};

	/**
	 * 調用抵用券訂單 API
	 */
	const getOverviewApi = async params => {
		const res = await postCampaignOverviewApi(params);
		if (res) setTableData(res);
		const par = { ...params, activeTab };
		updateUrlWithSearchQuery(par);
		preloadRef.current = true;
	};

	useEffect(
		() => {
			if (!preloadRef.current) return;
			preloadRef.current = false;
			const params = getParams(initBookmark(), activeTab);
			getOverviewApi(params);
		},
		// eslint-disable-next-line
		[activeTab]
	);

	useEffect(
		() => {
			if (!preloadRef.current) return;
			preloadRef.current = false;
			const params = getParams(bookmark, activeTab);
			getOverviewApi(params);
		},
		// eslint-disable-next-line
		[bookmark.pageIndex, activeTab]
	);

	// 重新整理||回上一頁後的功能
	useEffect(
		() => {
			if (isEmpty(campaignOptions)) return;
			if (refreshQueryRef.current) return;
			// 檢查 URL 中是否有查詢參數，如果沒有參數則不執行搜尋（首次進入）
			const search = history.location.search;
			const hasQueryParams = search && search.length > 0 && search !== "?";
			if (!hasQueryParams) {
				refreshQueryRef.current = true;
				return;
			}
			refreshQueryRef.current = true;
			const query = getQueryStringParams();
			const params = getParams(bookmark, activeTab);
			const newParams = { ...params, ...query };
			setParamsData(newParams);
			getOverviewApi(newParams);
		},
		// eslint-disable-next-line
		[campaignOptions, activeTab, bookmark.pageIndex]
	);

	useEffect(
		() => {
			getOptionsApi();
			setRefresh(handleSearchOnClick);
		},
		// eslint-disable-next-line
		[]
	);

	return (
		<Card className={clsx("search-bar-light p-3", className)}>
			<Card.CardContent>
				<Form onSubmit={handleSearchOnClick}>
					<Grid spacing={2} container>
						{/* 抵用券下拉選單 */}
						<Grid xs={8} item>
							{campaignOptions.length > 0 && (
								<SelectField key={campaignID} label={"抵用券"} ref={campaignRef} defaultValue={campaignID || campaignOptions[0]?.value} displayEmpty fullWidth>
									{Array.isArray(campaignOptions) &&
										campaignOptions
											.filter(item => item && !item.disabled)
											.map(item => (
												<MenuItem key={item.value} value={item.value}>
													{item.text}
												</MenuItem>
											))}
								</SelectField>
							)}
						</Grid>
						{/* 月份選擇器 */}
						<Grid xs={4} item>
							<DateField
								key={campaignMonth}
								ref={campaignMonthRef}
								views={["year", "month"]}
								openTo="month"
								format="YYYY/MM"
								label="月份"
								mask="____/__"
								defaultValue={campaignMonth ? parseDate(campaignMonth.split("T")[0], "YYYY-MM") : parseDate(new Date(), "YYYY-MM")}
								required
								fullWidth
							/>
						</Grid>
						<Grid xs={12} textAlign={"right"} item>
							<Button className={"white-space-nowrap"} type={"submit"} variant={"contained"}>
								搜尋
							</Button>
						</Grid>
					</Grid>
				</Form>
			</Card.CardContent>
		</Card>
	);
};

export default AccountBookCampaignOrderOverviewSearchBar;
